import { seekSupabase } from '@/integrations/supabase/seekClient';
import { parseSeekCvFunctionError } from '@/lib/seekCvParseErrors';

export const SEEK_CV_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const SEEK_CV_FILE_ACCEPT = '.pdf,.docx,.jpg,.jpeg,.png,.webp';

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const GENERIC_BROWSER_MIME_TYPES = new Set(['', 'application/octet-stream']);

export type SeekCvIngestionSource = 'onboarding' | 'profile_import';
export type SeekCvIngestionStage = 'uploaded' | 'document_created' | 'parsed';
export type SeekCvValidationCode = 'unsupported_type' | 'file_too_large' | 'empty_file';

export type SeekCvValidationResult =
  | { valid: true; extension: string; canonicalMimeType: string }
  | { valid: false; code: SeekCvValidationCode };

export interface SeekCvParseResult {
  parsed_data?: Record<string, unknown>;
  confidence?: Record<string, string>;
  import_id?: string;
  [key: string]: unknown;
}

export function validateSeekCvFile(file: Pick<File, 'name' | 'type' | 'size'>): SeekCvValidationResult {
  if (file.size === 0) return { valid: false, code: 'empty_file' };
  if (file.size > SEEK_CV_MAX_FILE_SIZE_BYTES) return { valid: false, code: 'file_too_large' };

  const extension = file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase() ?? '';
  const canonicalMimeType = MIME_BY_EXTENSION[extension];
  const suppliedMimeType = file.type.trim().toLowerCase().split(';', 1)[0];
  if (!canonicalMimeType || (!GENERIC_BROWSER_MIME_TYPES.has(suppliedMimeType) && suppliedMimeType !== canonicalMimeType)) {
    return { valid: false, code: 'unsupported_type' };
  }

  return { valid: true, extension, canonicalMimeType };
}

function sanitizeFilenameStem(filename: string): string {
  const basename = filename.replace(/\\/g, '/').split('/').pop() ?? 'cv';
  const stem = basename.replace(/\.[^.]+$/, '');
  const sanitized = stem
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}._-]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return sanitized || 'cv';
}

async function cleanupUnboundUpload(storageKey: string): Promise<void> {
  try {
    await seekSupabase.storage.from('candidate-documents').remove([storageKey]);
  } catch {
    // Best-effort cleanup; a failed cleanup must not hide the ingestion error.
  }
}

export async function ingestForkSeekCv(input: {
  candidateId: string;
  file: File;
  source: SeekCvIngestionSource;
  onStage?: (stage: SeekCvIngestionStage) => void;
}): Promise<{ documentId: string; storageKey: string; parsedData: SeekCvParseResult }> {
  const validation = validateSeekCvFile(input.file);
  if (validation.valid === false) throw new Error(validation.code);

  const { data: { session } } = await seekSupabase.auth.getSession();
  if (!session || session.user.id !== input.candidateId) throw new Error('not_authenticated');

  const storageKey = `${input.candidateId}/${input.source}-${Date.now()}-${crypto.randomUUID()}-${sanitizeFilenameStem(input.file.name)}.${validation.extension}`;
  let documentId: string | null = null;
  let documentLifecycleEstablished = false;

  const { error: uploadError } = await seekSupabase.storage
    .from('candidate-documents')
    .upload(storageKey, input.file, { contentType: validation.canonicalMimeType, upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  input.onStage?.('uploaded');

  try {
    const { data: document, error: documentError } = await seekSupabase
      .from('candidate_documents')
      .insert({
        candidate_id: input.candidateId,
        document_type: 'cv',
        original_filename: input.file.name,
        mime_type: validation.canonicalMimeType,
        file_size: input.file.size,
        storage_key: storageKey,
        upload_status: input.source === 'onboarding' ? 'complete' : 'uploaded',
        parse_status: 'pending',
      })
      .select('id')
      .single();
    if (documentError || !document?.id) throw new Error(documentError?.message || 'document_record_failed');
    documentId = document.id;
    documentLifecycleEstablished = true;
    input.onStage?.('document_created');

    const { data, error: parseError } = await seekSupabase.functions.invoke('seek-parse-cv', {
      body: {
        document_id: documentId,
        flow: input.source,
        ...(input.source === 'profile_import' ? { skip_profile_sync: true } : {}),
      },
    });
    if (parseError) {
      const friendly = await parseSeekCvFunctionError(parseError);
      throw new Error([friendly.message, friendly.supportText].filter(Boolean).join('\n'));
    }

    input.onStage?.('parsed');
    const parsedData = data && typeof data === 'object' && !Array.isArray(data)
      ? data as SeekCvParseResult
      : {};
    return { documentId, storageKey, parsedData };
  } catch (error) {
    if (!documentLifecycleEstablished) await cleanupUnboundUpload(storageKey);
    throw error;
  }
}
